import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '@/services/api';
import { selectAvatar as selectAvatarAPI } from '@/api/avatars';
import type { GameState, CreateGameRequest, SubmitTurnRequest } from '@/types';
import type { RootState } from './index';

interface GameStateSlice {
  currentGame: GameState | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: GameStateSlice = {
  currentGame: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const createGame = createAsyncThunk(
  'game/createGame',
  async (data: CreateGameRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.createGame(data);
      // Fetch full game state
      const gameState = await apiClient.getGameState(response.gameId);
      return gameState;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create game');
    }
  }
);

export const joinGame = createAsyncThunk(
  'game/joinGame',
  async (gameId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.joinGame(gameId);
      return response.gameState;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to join game');
    }
  }
);

export const fetchGameState = createAsyncThunk(
  'game/fetchGameState',
  async (gameId: string, { rejectWithValue }) => {
    try {
      const gameState = await apiClient.getGameState(gameId);
      return gameState;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch game state');
    }
  }
);

export const submitTurn = createAsyncThunk(
  'game/submitTurn',
  async (
    { gameId, turnData }: { gameId: string; turnData: SubmitTurnRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.submitTurn(gameId, turnData);
      return response.gameState;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit turn');
    }
  }
);

export const leaveGame = createAsyncThunk(
  'game/leaveGame',
  async (gameId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.leaveGame(gameId);
      return { success: response.success, gameId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to leave game');
    }
  }
);

export const selectAvatar = createAsyncThunk(
  'game/selectAvatar',
  async (
    { gameId, avatarId }: { gameId: string; avatarId: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.tokens?.accessToken;
      
      if (!accessToken) {
        return rejectWithValue('No access token available');
      }

      const response = await selectAvatarAPI(gameId, avatarId, accessToken);
      return response.gameState;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to select avatar');
    }
  }
);

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    updateGameState: (state, action: PayloadAction<GameState>) => {
      state.currentGame = action.payload;
    },
    clearGame: (state) => {
      state.currentGame = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Game
      .addCase(createGame.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createGame.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentGame = action.payload;
        state.error = null;
      })
      .addCase(createGame.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Join Game
      .addCase(joinGame.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(joinGame.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentGame = action.payload;
        state.error = null;
      })
      .addCase(joinGame.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Game State
      .addCase(fetchGameState.fulfilled, (state, action) => {
        state.currentGame = action.payload;
        state.error = null;
      })
      .addCase(fetchGameState.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Submit Turn
      .addCase(submitTurn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(submitTurn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentGame = action.payload;
        state.error = null;
      })
      .addCase(submitTurn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Leave Game
      .addCase(leaveGame.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(leaveGame.fulfilled, (state) => {
        state.isLoading = false;
        state.currentGame = null;
        state.error = null;
      })
      .addCase(leaveGame.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Select Avatar
      .addCase(selectAvatar.pending, (state) => {
        state.error = null;
      })
      .addCase(selectAvatar.fulfilled, (state, action) => {
        state.currentGame = action.payload;
        state.error = null;
      })
      .addCase(selectAvatar.rejected, (state, action) => {
        const message = String(action.payload || '');
        state.error = /already taken|already selected|not open/i.test(message) ? null : message;
      });
  },
});

export const { updateGameState, clearGame, clearError } = gameSlice.actions;
export const selectCurrentGame = (state: { game: GameStateSlice }) => state.game.currentGame;
export default gameSlice.reducer;

